import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  FormControlLabel,
  Checkbox,
  Fade,
  Grow
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const SPECIALITIES = [
  'General Physician',
  'Paediatrician',
  'Gynaecologist',
  'Dermatologist',
  'Cardiologist',
  'Dentist',
  'Orthopaedic Surgeon',
  'Ophthalmologist',
  'ENT Specialist',
  'Other Speciality'
];

const PREFERRED_TIMES = [
  { value: 'morning', label: 'Morning (10:00 AM - 1:00 PM)' },
  { value: 'afternoon', label: 'Afternoon (1:00 PM - 4:00 PM)' },
  { value: 'evening', label: 'Evening (4:00 PM - 7:00 PM)' }
];

export default function BookDemoModal({ open, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    clinicName: '',
    speciality: '',
    preferredTime: '',
    whatsappOptIn: true
  });

  const [errors, setErrors] = useState({});
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear errors when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Please enter your name';
    }
    
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!formData.phone) {
      newErrors.phone = 'Mobile number is required';
    } else if (!phoneRegex.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit Indian mobile number';
    }

    if (!formData.clinicName.trim()) {
      newErrors.clinicName = 'Please enter your clinic or hospital name';
    }

    if (!formData.speciality) {
      newErrors.speciality = 'Please select your medical speciality';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setSubmitting(false);
      setIsSuccess(true);
    }, 1200);
  };

  const handleReset = () => {
    setFormData({
      name: '',
      phone: '',
      clinicName: '',
      speciality: '',
      preferredTime: '',
      whatsappOptIn: true
    });
    setErrors({});
    setIsSuccess(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={isSuccess ? handleReset : onClose}
      maxWidth="sm"
      fullWidth
      scroll="body"
      TransitionComponent={Grow}
      transitionDuration={400}
      PaperProps={{
        sx: {
          borderRadius: '20px',
          boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
          overflow: 'hidden',
          p: 1
        }
      }}
    >
      {/* Top Banner Accent */}
      <Box sx={{ height: '6px', background: 'linear-gradient(90deg, #10b981 0%, #3b82f6 50%, #4f46e5 100%)' }} />
      
      {!isSuccess ? (
        <>
          <DialogTitle sx={{ m: 0, p: 3, pb: 1, position: 'relative' }}>
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 700,
                fontFamily: 'Outfit, sans-serif',
                color: '#1e293b',
                lineHeight: 1.2
              }}
            >
              Schedule a Personalised Demo
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: '#64748b',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontWeight: 500
              }}
            >
              See how Care Beyond transforms your practice and automates patient engagement on WhatsApp.
            </Typography>
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: 16,
                top: 16,
                color: '#94a3b8',
                '&:hover': { color: '#64748b' }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3, pt: 1, pb: 2 }}>
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="name"
                label="Doctor's Name"
                name="name"
                placeholder="Dr. Rajesh Kumar"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                  }
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="phone"
                label="Mobile Number (WhatsApp)"
                name="phone"
                placeholder="e.g. 9876543210"
                value={formData.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone || "Required for WhatsApp integration & scheduling details"}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                  }
                }}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="clinicName"
                label="Clinic / Hospital Name"
                name="clinicName"
                placeholder="e.g. Care Point Clinic"
                value={formData.clinicName}
                onChange={handleChange}
                error={!!errors.clinicName}
                helperText={errors.clinicName}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                  }
                }}
              />

              <TextField
                select
                margin="normal"
                required
                fullWidth
                id="speciality"
                label="Speciality"
                name="speciality"
                value={formData.speciality}
                onChange={handleChange}
                error={!!errors.speciality}
                helperText={errors.speciality}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                  }
                }}
              >
                {SPECIALITIES.map((spec) => (
                  <MenuItem key={spec} value={spec} sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {spec}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                margin="normal"
                fullWidth
                id="preferredTime"
                label="Preferred Callback Time"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                  }
                }}
              >
                <MenuItem value="" sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  <em>Anytime (10:00 AM - 7:00 PM)</em>
                </MenuItem>
                {PREFERRED_TIMES.map((time) => (
                  <MenuItem key={time.value} value={time.value} sx={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    {time.label}
                  </MenuItem>
                ))}
              </TextField>

              <Box
                sx={{
                  mt: 2.5,
                  p: 2,
                  borderRadius: '12px',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #dcfce7',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}
              >
                <WhatsAppIcon sx={{ color: '#25D366', mt: '2px' }} />
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="whatsappOptIn"
                        checked={formData.whatsappOptIn}
                        onChange={handleChange}
                        sx={{
                          p: 0,
                          mr: 1,
                          color: '#25D366',
                          '&.Mui-checked': { color: '#25D366' }
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#14532d',
                          fontFamily: 'Plus Jakarta Sans, sans-serif',
                          userSelect: 'none'
                        }}
                      >
                        Receive demo link and schedules on WhatsApp
                      </Typography>
                    }
                    sx={{ m: 0 }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      color: '#15803d',
                      fontFamily: 'Plus Jakarta Sans, sans-serif',
                      fontWeight: 500
                    }}
                  >
                    We value your privacy. No spam. Only secure clinic updates & credentials.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 1, gap: 1.5 }}>
            <Button
              onClick={onClose}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: '10px',
                color: '#64748b',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': { backgroundColor: '#f1f5f9' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={submitting}
              sx={{
                px: 4,
                py: 1.2,
                borderRadius: '10px',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#3b82f6',
                backgroundImage: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
                '&:hover': {
                  backgroundColor: '#2563eb',
                  backgroundImage: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.6)'
                }
              }}
            >
              {submitting ? 'Scheduling...' : 'Confirm Booking'}
            </Button>
          </DialogActions>
        </>
      ) : (
        <Fade in={isSuccess}>
          <Box
            sx={{
              p: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                backgroundColor: '#ecfdf5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 3,
                border: '2px solid #a7f3d0'
              }}
            >
              <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontFamily: 'Outfit, sans-serif',
                color: '#1e293b',
                mb: 1.5
              }}
            >
              Demo Booked Successfully!
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontWeight: 500,
                maxWidth: '420px',
                lineHeight: 1.6,
                mb: 4
              }}
            >
              Thank you, <strong>{formData.name.toLowerCase().startsWith('dr') ? formData.name : `Dr. ${formData.name}`}</strong>.<br />
              Our practice specialist will call you at <strong>+91 {formData.phone}</strong> {formData.preferredTime ? `during the ${formData.preferredTime}` : 'shortly'} to set up your account.
            </Typography>

            {formData.whatsappOptIn && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 3,
                  py: 1.5,
                  borderRadius: '30px',
                  backgroundColor: '#e6fbf0',
                  color: '#15803d',
                  border: '1px solid #bbf7d0',
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  mb: 4
                }}
              >
                <WhatsAppIcon sx={{ fontSize: '20px', color: '#25D366' }} />
                Confirmation details sent to WhatsApp!
              </Box>
            )}

            <Button
              onClick={handleReset}
              variant="outlined"
              sx={{
                px: 4,
                py: 1,
                borderRadius: '10px',
                borderColor: '#cbd5e1',
                color: '#64748b',
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontWeight: 600,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#94a3b8',
                  backgroundColor: '#f8fafc'
                }
              }}
            >
              Close
            </Button>
          </Box>
        </Fade>
      )}
    </Dialog>
  );
}
